import React, {useEffect, useState, useCallback} from 'react';
import { useTitle } from "../../hooks/useTitle";
import { Alert, Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { FaExpandAlt, FaRegEdit } from "react-icons/fa";
import { FaRegTrashCan } from "react-icons/fa6";
import CategoriesService from "../../http/CategoriesService";
import Loading from "../../components/Loading";
import CategoryModalDelete from "../../components/modals/CategoryModalDelete";
import CategoryModalEdite from "../../components/modals/CategoryModalEdite";
import {useAuth} from "../../store/AuthContext";

const AdminCategories: React.FC = () => {
    useTitle("Админ/Категории");
    const {user, isAuth, isLoading} = useAuth();

    const [loading, setLoading] = useState<boolean>(true);
    const [categories, setCategories] = useState<any[] | null>(null);
    const [categoryId, setCategoryId] = useState<number | string | null>(null);

    // Ошибки и уведомления
    const [error, setError] = useState<string | null>(null);
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Поля создания
    const [name, setName] = useState<string>("");
    const [validated, setValidated] = useState(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);

    // Состояния модалок
    const [showEdite, setShowEdite] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showCreate, setShowCreate] = useState(false);

    // Загрузка данных (обернута в useCallback для стабильности)
    const fetchCategories = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await CategoriesService.query({
                options: {
                    where: { action: null },
                    order: [['id', 'DESC']]
                }
            });
            setCategories(data);
        } catch (err: any) {
            // 401 обработает интерцептор, здесь ловим только текст ошибки
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (isLoading || !isAuth) return;
            void fetchCategories();
        };

        void fetchData();
    }, [fetchCategories, isAuth, isLoading]);


    const handleSubmitCreate = async (event: React.SubmitEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const fields = {
                name: name,
                userId: user.id
            };

            await CategoriesService.create(fields);
            setSuccess("Категория добавлена");
            setShowCreate(false);
            setValidated(false);
            await fetchCategories();
        } catch (err: any) {
            setErrorForm(err.response?.data?.message || err.message);
        }
    };

    if (isLoading || loading) {
        return <Loading />;
    }

    return (
        <>
            <div className={"d-flex jc-between mb-3"}>
                <h4 className={"mb-0"}>Категории</h4>
                <div>
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            setShowCreate(true);
                            setErrorForm(null);
                            setValidated(false);
                            setName("");
                        }}
                    >
                        <FiPlus className={"me-2"} />Добавить
                    </Button>
                </div>
            </div>

            {error && <Alert variant={"danger"} dismissible={true} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant={"success"} dismissible={true} onClose={() => setSuccess(null)}>{success}</Alert>}

            {categories && categories.length > 0 ? (
                <div className={"table-body scrollarea"}>
                    <Table bordered={true} hover={true} size={"sm"} className={"full"}>
                        <thead>
                        <tr>
                            <th style={{width: "1%"}}>ID</th>
                            <th>Название</th>
                            <th style={{width: "145px"}}>Создан</th>
                            <th style={{width: "60px"}}></th>
                        </tr>
                        </thead>
                        <tbody>
                        {categories.map((category: any) => (
                            <tr key={category.id}>
                                <td>{category.id}</td>
                                <td>{category.name}</td>
                                <td>{new Date(category.createdAt).toLocaleString()}</td>
                                <td>
                                    <div className={"d-flex ai-center gap-3"}>
                                        <FaRegEdit title={"Редактирование"} className={"pointer text-primary"}
                                                   onClick={() => {
                                                       setCategoryId(category.id);
                                                       setShowEdite(true);
                                                   }}
                                        />
                                        <FaRegTrashCan
                                            title={"Удалить"}
                                            className={"pointer text-danger"}
                                            onClick={() => {
                                                setCategoryId(category.id);
                                                setShowDelete(true);
                                            }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <Alert variant={"secondary"}>Список категорий пуст</Alert>
            )}

            {/* Модалка создания */}
            <Modal
                show={showCreate}
                size={"lg"}
                fullscreen={fullscreen ? true : "md-down"}
                scrollable={true}
                onHide={() => setShowCreate(false)}
            >
                <div className={"modal-header"}>
                    <h5 className={"mb-0"}>Добавление категории</h5>
                    <div className={"d-flex gap-3 ai-center"}>
                        <button type={"button"} className={"btn-link border-0 bg-transparent"} onClick={() => setFullscreen(!fullscreen)}>
                            <FaExpandAlt />
                        </button>
                        <button type={"button"} className={"btn-close"} onClick={() => setShowCreate(false)}></button>
                    </div>
                </div>
                <Form noValidate={true} validated={validated} onSubmit={handleSubmitCreate}>
                    <Modal.Body>
                        {errorForm && <Alert variant={"danger"} dismissible={true} onClose={() => setErrorForm(null)}>{errorForm}</Alert>}
                        <Form.Group as={Row} className={"mb-3"} controlId={"name"}>
                            <Form.Label column={true} sm={"2"}>
                                Название <span className={"text-danger"}>*</span>
                            </Form.Label>
                            <Col sm={"10"}>
                                <Form.Control
                                    type={"text"}
                                    autoComplete={"off"}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required={true}
                                />
                                <Form.Control.Feedback type={"invalid"}>
                                    Поле обязательно для заполнения
                                </Form.Control.Feedback>
                            </Col>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant={"primary"} type={"submit"}>Сохранить</Button>
                        <Button variant={"secondary"} onClick={() => setShowCreate(false)}>Отмена</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Внешние компоненты модалок */}
            {showEdite && categoryId && (
                <CategoryModalEdite visible={showEdite} setVisible={setShowEdite} categoryId={categoryId} refreshTable={fetchCategories}/>
            )}
            {showDelete && categoryId && (
                <CategoryModalDelete visible={showDelete} setVisible={setShowDelete} categoryId={categoryId} refreshTable={fetchCategories}/>
            )}
        </>
    );
};

export default AdminCategories;
